import { useMemo, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { toast } from "react-toastify";
import { Trans } from "react-i18next";

import { pushAnalyticsEvent } from "@otta/analytics";
import { useQuery } from "@otta/search/apollo";
import { Loading } from "@otta/search/components/Loading";
import { Question, Quiz } from "@otta/search/containers/Quiz";
import {
  UK_LOCATIONS,
  EU_LOCATIONS,
  US_LOCATIONS,
} from "@otta/search/constants/locations";
import {
  PopularTechnologiesUsedDocument,
  UserCvParsingStatusDocument,
} from "@otta/search/schema";
import { CompanyQuality } from "@otta/search/containers/Quiz/questions/CompanyQuality";
import { CompanySizePreference } from "@otta/search/containers/Quiz/questions/CompanySizePreference";
import { Demographic } from "@otta/search/containers/Quiz/questions/Demographic";
import { EmailNotifications } from "@otta/search/containers/Quiz/questions/EmailNotifications";
import { JobExperience } from "@otta/search/containers/Quiz/questions/JobExperience";
import { JobFunctionSubFunction } from "@otta/search/containers/Quiz/questions/JobFunctionSubFunction";
import { LanguageRequirement } from "@otta/search/containers/Quiz/questions/LanguageRequirement";
import { LinkedinUrl } from "@otta/search/containers/Quiz/questions/LinkedinUrl";
import { Location } from "@otta/search/containers/Quiz/questions/Location";
import { NegativeTechnologiesPreference } from "@otta/search/containers/Quiz/questions/NegativeTechnologiesPreference";
import { PositiveTechnologiesPreference } from "@otta/search/containers/Quiz/questions/PositiveTechnologiesPreference";
import { SalaryPreference } from "@otta/search/containers/Quiz/questions/SalaryPreference";
import { SearchStage } from "@otta/search/containers/Quiz/questions/SearchStage";
import { PositiveSectorTagsPreference } from "@otta/search/containers/Quiz/questions/PositiveSectorTagsPreference";
import { NegativeSectorTagsPreference } from "@otta/search/containers/Quiz/questions/NegativeSectorTagsPreference";
import { VisaSponsorship } from "@otta/search/containers/Quiz/questions/VisaSponsorship";
import { useUserPreferences } from "@otta/search/providers/UserPreferencesProvider/useUserPreferences";
import { SignUp } from "@otta/search/containers/Quiz/questions/SignUp";
import { SignUpReason } from "@otta/search/containers/Quiz/questions/SignUpReason";
import { RemoteLocation } from "@otta/search/containers/Quiz/questions/RemoteLocation";
import { CurrentAddress } from "@otta/search/containers/Quiz/questions/CurrentAddress";
import { CVUpload } from "@otta/search/containers/Quiz/questions/CVUpload";
import { PreferencesReview } from "@otta/search/containers/Quiz/questions/PreferencesReview";
import { useExperiment } from "@otta/search/hooks/unleash";
import { Experiment } from "@otta/search/constants/experiments";
import { Icon } from "@otta/icons";
import { Text } from "@otta/design";

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

const experimentQuizQuestions = (
  currentAddressExperimentEnabled: boolean,
  onlyRemoteOpportunities: boolean,
  multipleLocations: boolean,
  showTechnologies: boolean,
  loggedIn: boolean,
  showCvUploadQuestions: boolean,
  allowCvUpload: boolean,
  CvUploaded: boolean
): Question[] => {
  const questionsToRender: (false | Question)[] = [
    {
      title: "Sign up to see your best matches",
      subtitle: "View, save, and apply for jobs by creating an account",
      component: SignUp,
      path: "sign-up",
      skip: loggedIn,
    },
    {
      title: (
        <Wrapper>
          Tell us who you are<Icon icon="new-to-otta"></Icon>
        </Wrapper>
      ),
      component: CVUpload,
      path: "cv-upload",
      skip: !allowCvUpload,
    },
    {
      title: "Does this look right?",
      subtitle: (
        <Text size={0}>
          <Trans>
            We've completed your search preferences with your CV and the help of
            AI.
          </Trans>
          <br />
          If something looks incorrect, simply click to edit it directly on the
          screen.
        </Text>
      ),
      component: PreferencesReview,
      path: "preferences-review",
      skip: !showCvUploadQuestions && !CvUploaded,
    },
    {
      title: "Which 3 are most important to you in a new role?",
      component: CompanyQuality,
      path: "quality",
      skip: showCvUploadQuestions || CvUploaded,
    },
    currentAddressExperimentEnabled && {
      title: "Where do you live?",
      subtitle: "This will help us personalise your experience",
      component: CurrentAddress,
      path: "current-location",
      skip: showCvUploadQuestions || CvUploaded,
    },
    {
      title: "Where would you like to work?",
      subtitle: "Add all the cities you would commute to",
      component: Location,
      path: "location",
      skip: showCvUploadQuestions || CvUploaded,
    },
    onlyRemoteOpportunities && {
      title: "Where could you work from remotely?",
      component: RemoteLocation,
      path: "remote-location",
      skip: showCvUploadQuestions || CvUploaded,
    },
    {
      title: multipleLocations
        ? "Do you require a visa to work in these locations?"
        : `Do you require a visa to work in this location?`,
      component: VisaSponsorship,
      path: "visa-sponsorship",
      skip: showCvUploadQuestions || CvUploaded,
    },
    {
      title: "Which languages can you work in?",
      component: LanguageRequirement,
      path: "language-requirement",
      skip: showCvUploadQuestions || CvUploaded,
    },
    {
      title: "When are you looking to start your new job?",
      component: SearchStage,
      path: "search-stage",
    },
    {
      title: "What types of roles would you like to see?",
      component: JobFunctionSubFunction,
      path: "function",
      skip: showCvUploadQuestions || CvUploaded,
    },
    {
      title: "What level of roles would you like to see?",
      component: JobExperience,
      subtitle: "Select the most relevant for you (max 2)",
      path: "job-experience",
      skip: showCvUploadQuestions || CvUploaded,
    },
    {
      title: "Do you want to work for a specific size of company?",
      component: CompanySizePreference,
      path: "company-size",
      skip: showCvUploadQuestions || CvUploaded,
    },
    {
      title: "Do you have any favourite industries in mind?",
      component: PositiveSectorTagsPreference,
      path: "positive-company-sectors",
      skip: showCvUploadQuestions || CvUploaded,
    },
    {
      title: "Are there any industries you don't want to work in?",
      component: NegativeSectorTagsPreference,
      path: "negative-company-sectors",
      skip: showCvUploadQuestions || CvUploaded,
    },
    {
      title: "Do you have any favourite technologies in mind?",
      component: PositiveTechnologiesPreference,
      path: "positive-technologies",
      skip: (!showTechnologies && showCvUploadQuestions) || CvUploaded,
    },
    {
      title: "Are there any technologies you don't want to use at work?",
      component: NegativeTechnologiesPreference,
      path: "negative-technologies",
      skip: (!showTechnologies && showCvUploadQuestions) || CvUploaded,
    },
    {
      title: "What is your minimum expected salary?",
      component: SalaryPreference,
      path: "salary",
      skip: showCvUploadQuestions || CvUploaded,
    },
    {
      title: "More about you (optional)",
      component: Demographic,
      path: "demographic",
    },
    {
      title:
        "Would you like to be contacted directly by companies that match your preferences?",
      component: LinkedinUrl,
      path: "linkedin-profile",
    },
    {
      title: "When would you like to be emailed about new jobs?",
      component: EmailNotifications,
      path: "email-notifications",
    },
    {
      title: "Where did you hear about us?",
      component: SignUpReason,
      path: "signup-reason",
    },
  ];

  return questionsToRender.filter((question): question is Question =>
    Boolean(question)
  );
};

export const standardQuizQuestions = (
  currentAddressExperimentEnabled: boolean,
  onlyRemoteOpportunities: boolean,
  multipleLocations: boolean,
  showTechnologies: boolean,
  loggedIn: boolean
): Question[] => {
  const questionsToRender: (false | Question)[] = [
    {
      title: "Which 3 are most important to you in a new role?",
      component: CompanyQuality,
      path: "quality",
    },
    currentAddressExperimentEnabled && {
      title: "Where do you live?",
      subtitle: "This will help us personalise your experience",
      component: CurrentAddress,
      path: "current-location",
    },
    {
      title: "Where would you like to work?",
      subtitle: "Add all the cities you would commute to",
      component: Location,
      path: "location",
    },
    onlyRemoteOpportunities && {
      title: "Where could you work from remotely?",
      component: RemoteLocation,
      path: "remote-location",
    },
    {
      title: multipleLocations
        ? "Do you require a visa to work in these locations?"
        : `Do you require a visa to work in this location?`,
      component: VisaSponsorship,
      path: "visa-sponsorship",
    },
    {
      title: "Which languages can you work in?",
      component: LanguageRequirement,
      path: "language-requirement",
    },
    {
      title: "When are you looking to start your new job?",
      component: SearchStage,
      path: "search-stage",
    },
    {
      title: "What types of roles would you like to see?",
      component: JobFunctionSubFunction,
      path: "function",
    },
    {
      title: "What level of roles would you like to see?",
      component: JobExperience,
      subtitle: "Select the most relevant for you (max 2)",
      path: "job-experience",
    },
    {
      title: "Do you want to work for a specific size of company?",
      component: CompanySizePreference,
      path: "company-size",
    },
    {
      title: "Do you have any favourite industries in mind?",
      component: PositiveSectorTagsPreference,
      path: "positive-company-sectors",
    },
    {
      title: "Are there any industries you don't want to work in?",
      component: NegativeSectorTagsPreference,
      path: "negative-company-sectors",
    },
    {
      title: "Do you have any favourite technologies in mind?",
      component: PositiveTechnologiesPreference,
      path: "positive-technologies",
      skip: !showTechnologies,
    },
    {
      title: "Are there any technologies you don't want to use at work?",
      component: NegativeTechnologiesPreference,
      path: "negative-technologies",
      skip: !showTechnologies,
    },
    {
      title: "What is your minimum expected salary?",
      component: SalaryPreference,
      path: "salary",
    },
    {
      title: "More about you (optional)",
      component: Demographic,
      path: "demographic",
    },
    {
      title: "One more thing...",
      subtitle: "View, save, and apply for jobs by creating an account",
      component: SignUp,
      path: "sign-up",
      skip: loggedIn,
    },
    {
      title:
        "Would you like to be contacted directly by companies that match your preferences?",
      component: LinkedinUrl,
      path: "linkedin-profile",
    },
    {
      title: "When would you like to be emailed about new jobs?",
      component: EmailNotifications,
      path: "email-notifications",
    },
    {
      title: "Where did you hear about us?",
      component: SignUpReason,
      path: "signup-reason",
    },
  ];

  return questionsToRender.filter((question): question is Question =>
    Boolean(question)
  );
};

export default function InitialPreferences(): React.ReactElement {
  const navigate = useNavigate();
  const { state: locationState, pathname } = useLocation();
  const [noCvRedirect, setNoCvRedirect] = useState(false);

  const { variant } = useExperiment(Experiment.CurrentAddress);
  const currentAddressExperimentEnabled = variant === "variant";

  const { variant: CvSignupVariant } = useExperiment(Experiment.CVSignup);
  const CvSignupEnabled = CvSignupVariant === "variant";
  const { data: userData, loading: userLoading } = useQuery(
    UserCvParsingStatusDocument
  );

  const { preferencesData, loggedIn, loading, onlyRemoteOpportunities } =
    useUserPreferences();

  useEffect(() => {
    if (locationState?.toast) {
      toast.error(locationState.toast.message);
      // emptying locationState.toast to avoid showing the toast again when rerendered
      navigate(pathname, {
        replace: true,
        state: { ...locationState, toast: undefined },
      });
    }

    if (locationState?.noCvRedirect) {
      pushAnalyticsEvent({ eventName: "Candidate Progressed without CV" });
      setNoCvRedirect(true);
    }

    if (locationState?.cvParsingFailed) {
      pushAnalyticsEvent({
        eventName: "Candidate Progressed without CV (failed parsing)",
      });
    }

    if (pathname === "/initial-preferences/cv-upload") {
      setNoCvRedirect(false);
    }
  }, [locationState, pathname, navigate]);

  const {
    data: popularTechnologiesData,
    previousData: previousPopularTechnologiesData,
  } = useQuery(PopularTechnologiesUsedDocument, {
    skip: !preferencesData,
    variables: {
      subFunctions: (preferencesData?.jobSubFunctionPreferences ?? []).map(
        pref => pref.subFunctionId
      ),
    },
  });

  const locationPreferences = useMemo(
    () => (preferencesData?.locationPreferences ?? []).map(l => l.location),
    [preferencesData?.locationPreferences]
  );

  if (!preferencesData || userLoading || loading) {
    return <Loading />;
  }

  const showTechnologies =
    (
      popularTechnologiesData?.popularTechnologiesUsed ??
      previousPopularTechnologiesData?.popularTechnologiesUsed ??
      []
    ).length > 5;

  const multipleLocations =
    [
      locationPreferences.find(location => UK_LOCATIONS.has(location)),
      locationPreferences.find(location => US_LOCATIONS.has(location)),
      locationPreferences.find(location => EU_LOCATIONS.has(location)),
    ].filter(location => location).length > 1;

  const handleFinish = () => {
    localStorage.setItem("first_visit", "true");
    navigate("/", { replace: true });
  };

  const CvUploaded = userData?.currentUser?.cvParsingStatus === "COMPLETED";

  // These two booleans control the display of CV upload questions
  // and the ability to allow the user to go back upload a CV if the user
  // clicked on "I don't have a CV" but decided to go back
  const showCvUploadQuestions =
    userData?.currentUser?.cvParsingStatus === "FAILED"
      ? false
      : CvSignupEnabled && !CvUploaded && !noCvRedirect;
  const allowCvUpload =
    userData?.currentUser?.cvParsingStatus === "FAILED"
      ? false
      : CvSignupEnabled && !CvUploaded;

  return (
    <Quiz
      questions={
        CvSignupEnabled
          ? experimentQuizQuestions(
              currentAddressExperimentEnabled,
              onlyRemoteOpportunities,
              multipleLocations,
              showTechnologies,
              loggedIn,
              showCvUploadQuestions,
              allowCvUpload,
              CvUploaded
            )
          : standardQuizQuestions(
              currentAddressExperimentEnabled,
              onlyRemoteOpportunities,
              multipleLocations,
              showTechnologies,
              loggedIn
            )
      }
      finishText="Finish"
      analyticsName="initial-preferences"
      data-testid="initial-preferences-quiz"
      onFinish={handleFinish}
      animated
      progressBanner
    />
  );
}
